/**
 * Compass needle spinner — a brand-aligned loading indicator.
 * The needle sweeps and settles to "true North" when mounted.
 */
import { useEffect, useState } from 'react';

export function CompassSpinner({
  size = 40,
  className = '',
}: {
  size?: number;
  className?: string;
}) {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    // Brief delay lets the initial off-bearing render paint before transition starts
    const t = setTimeout(() => setSettled(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    >
      <svg
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
      >
        {/* Outer ring */}
        <circle cx="20" cy="20" r="18" stroke="#D9E8DC" strokeWidth="1.5" />
        {/* Cardinal tick marks */}
        <line x1="20" y1="3" x2="20" y2="7.5" stroke="#5A7D5E" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="20" y1="32.5" x2="20" y2="37" stroke="#B5D1BA" strokeWidth="1" strokeLinecap="round" />
        <line x1="3" y1="20" x2="7.5" y2="20" stroke="#B5D1BA" strokeWidth="1" strokeLinecap="round" />
        <line x1="32.5" y1="20" x2="37" y2="20" stroke="#B5D1BA" strokeWidth="1" strokeLinecap="round" />
        {/* Intercardinal ticks (lighter) */}
        <line x1="7.3" y1="7.3" x2="9.9" y2="9.9" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round" />
        <line x1="30.1" y1="7.3" x2="32.7" y2="9.9" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round" />
        <line x1="7.3" y1="32.7" x2="9.9" y2="30.1" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round" />
        <line x1="30.1" y1="32.7" x2="32.7" y2="30.1" stroke="#D9E8DC" strokeWidth="0.8" strokeLinecap="round" />
        {/* Inner ring */}
        <circle cx="20" cy="20" r="10" stroke="#EBF4EC" strokeWidth="0.8" />
        {/* Needle group — rotates from off-bearing → true North */}
        <g
          style={{
            transformOrigin: '20px 20px',
            transform: settled ? 'rotate(0deg)' : 'rotate(-140deg)',
            transition: settled
              ? 'transform 0.85s cubic-bezier(0.34, 1.56, 0.64, 1)'
              : 'none',
          }}
        >
          {/* North point (sage green) */}
          <polygon points="20,7 17.8,20 20,21.8 22.2,20" fill="#5A7D5E" />
          {/* South point (light) */}
          <polygon points="20,33 17.8,20 20,21.8 22.2,20" fill="#C8DEC9" />
          {/* Pivot dot */}
          <circle cx="20" cy="20" r="2.2" fill="white" stroke="#5A7D5E" strokeWidth="1.2" />
        </g>
        {/* North dot indicator */}
        <circle cx="20" cy="5" r="1.8" fill="#5A7D5E" />
      </svg>
    </div>
  );
}
